"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, Loader2, ShieldCheck, Clock, Award } from "lucide-react";
import type { Product } from "@/data/products-page";
import { useScrollLock } from "./useScrollLock";

const BRAND_COLOR = "#0a0a0a";

interface EnquiryModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  quantity: string;
  message: string;
}

const initialForm: FormData = {
  name: "",
  email: "",
  company: "",
  phone: "",
  quantity: "",
  message: "",
};

type FormStatus = "idle" | "submitting" | "success";

export default function EnquiryModal({
  product,
  isOpen,
  onClose,
}: EnquiryModalProps) {
  const [form, setForm] = useState<FormData>(initialForm);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Lock scroll when open
  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) setTimeout(() => firstInputRef.current?.focus(), 300);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setForm(initialForm);
      setErrors({});
      setStatus("idle");
    }
  }, [isOpen, product?.id]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!form.name.trim()) newErrors.name = "Full name is required";
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!form.company.trim()) newErrors.company = "Company name is required";

    // 🔒 Phone field is now REQUIRED with phone number validation
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/.test(form.phone.trim())) {
      newErrors.phone = "Enter a valid phone number";
    }

    if (!form.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData])
      setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!product) return;

    setStatus("submitting");
    setSubmitError(null);

    try {
      const res = await fetch("/api/forms/productEnquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          productTitle: product.title,
          productCategory: product.category,
        }),
      });

      if (!res.ok) {
        const result = await res.json().catch(() => ({}));
        throw new Error(result.error || "Failed to send enquiry.");
      }

      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setSubmitError(
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      );
    }
  };

  if (!product) return null;

  const inputCls =
    "w-full rounded-lg border border-neutral-200 bg-neutral-50/70 px-3.5 py-2.5 text-xs text-neutral-900 outline-none transition-all placeholder:text-neutral-400 focus:bg-white focus:shadow-[0_0_0_3px_rgba(10,10,10,0.08)] focus:border-neutral-900";
  const errorCls = "!border-red-400 bg-red-50/50";
  const labelCls = "text-[0.68rem] font-bold tracking-wider uppercase text-neutral-600";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex h-screen w-screen flex-col bg-neutral-950/20 backdrop-blur-md overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-label={`Enquiry for ${product.title}`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="flex h-full w-full flex-col bg-white overflow-hidden">
            {/* ── TOP HEADER ── */}
            <header className="flex shrink-0 items-center justify-between border-b border-neutral-100 bg-white/80 px-6 py-3.5 sm:px-10 backdrop-blur-md">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                </span>
                <span className="text-[0.7rem] font-bold uppercase tracking-widest text-neutral-500">
                  Direct Product Enquiry
                </span>
              </div>
              <button
                onClick={onClose}
                className="group flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition hover:bg-neutral-900 hover:text-white"
                aria-label="Close enquiry popup"
              >
                <X size={16} className="transition-transform group-hover:rotate-90" />
              </button>
            </header>

            {/* ── MAIN CONTENT GRID (NO DESKTOP SCROLL) ── */}
            <div className="flex flex-1 items-center justify-center px-6 py-4 max-md:overflow-y-auto sm:px-12">
              <div className="w-full max-w-6xl">
                {status === "success" ? (
                  <motion.div
                    className="flex flex-col items-center justify-center gap-4 py-10 text-center"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shadow-sm">
                      <CheckCircle2 size={36} strokeWidth={1.75} />
                    </div>
                    <h2 className="text-2xl font-extrabold tracking-tight text-neutral-950 sm:text-3xl">
                      Enquiry Received
                    </h2>
                    <p className="max-w-md text-xs leading-relaxed text-neutral-600">
                      Thank you for contacting us regarding <strong>{product.title}</strong>. A dedicated product technical advisor will review your specs and contact you within 24 hours.
                    </p>
                    <motion.button
                      className="mt-2 rounded-lg px-8 py-2.5 text-xs font-bold tracking-wider text-white shadow-md transition-all hover:shadow-lg"
                      style={{ backgroundColor: BRAND_COLOR }}
                      onClick={onClose}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Close Window
                    </motion.button>
                  </motion.div>
                ) : (
                  <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-12 md:gap-12">
                    {/* LEFT COLUMN: Product Highlight Card */}
                    <div className="md:col-span-4 flex flex-col justify-between rounded-2xl bg-neutral-900 p-6 text-white shadow-xl max-md:hidden">
                      <div>
                        <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-widest text-neutral-300">
                          {product.category}
                        </span>
                        <h2 className="mt-3 text-xl font-extrabold leading-tight tracking-tight text-white">
                          {product.title}
                        </h2>
                        <p className="mt-2 text-xs leading-relaxed text-neutral-400 line-clamp-3">
                          Request custom pricing, bulk volume availability, and technical datasheets directly from our technical team.
                        </p>
                      </div>

                      <div className="mt-8 space-y-3.5 border-t border-neutral-800 pt-6">
                        <div className="flex items-center gap-3 text-xs text-neutral-300">
                          <Clock size={15} className="text-neutral-400 shrink-0" />
                          <span>Response within 1 business day</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-neutral-300">
                          <Award size={15} className="text-neutral-400 shrink-0" />
                          <span>Official Enterprise Supplier</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-neutral-300">
                          <ShieldCheck size={15} className="text-neutral-400 shrink-0" />
                          <span>Encrypted & Confidential</span>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COLUMN: Interactive Form */}
                    <div className="md:col-span-8">
                      <div className="mb-4 text-left">
                        <div className="md:hidden">
                          <p className="text-[0.65rem] font-bold uppercase tracking-widest text-neutral-400">
                            {product.category}
                          </p>
                          <h1 className="text-xl font-extrabold text-neutral-950">
                            {product.title}
                          </h1>
                        </div>
                        <h2 className="text-lg font-bold tracking-tight text-neutral-950 max-md:hidden">
                          Send an Inquiry
                        </h2>
                        <p className="text-xs text-neutral-500">
                          Please fill out your details to receive specialized specifications and quotes.
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5" noValidate>
                        {/* Row 1: Full Name & Email */}
                        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                          <Field
                            label="Full Name" name="name" type="text" placeholder="John Smith"
                            value={form.name} error={errors.name} onChange={handleChange}
                            ref={firstInputRef} inputCls={inputCls} errorCls={errorCls} labelCls={labelCls} required
                          />
                          <Field
                            label="Email Address" name="email" type="email" placeholder="john@company.com"
                            value={form.email} error={errors.email} onChange={handleChange}
                            inputCls={inputCls} errorCls={errorCls} labelCls={labelCls} required
                          />
                        </div>

                        {/* Row 2: Company & Phone (NOW REQUIRED) */}
                        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                          <Field
                            label="Company Name" name="company" type="text" placeholder="Acme Corp"
                            value={form.company} error={errors.company} onChange={handleChange}
                            inputCls={inputCls} errorCls={errorCls} labelCls={labelCls} required
                          />
                          <Field
                            label="Phone Number" name="phone" type="tel" placeholder="+1 (555) 000-0000"
                            value={form.phone} error={errors.phone} onChange={handleChange}
                            inputCls={inputCls} errorCls={errorCls} labelCls={labelCls} required
                          />
                        </div>

                        {/* Row 3: Quantity */}
                        <Field
                          label="Estimated Quantity / Units Required" name="quantity" type="text"
                          placeholder="e.g. 50 units, batch order, or evaluation unit"
                          value={form.quantity} error={errors.quantity} onChange={handleChange}
                          inputCls={inputCls} errorCls={errorCls} labelCls={labelCls}
                        />

                        {/* Row 4: Message */}
                        <div className="flex flex-col gap-1">
                          <label className={labelCls} htmlFor="message">
                            Requirements / Message <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            rows={3}
                            placeholder="Tell us about your project requirements, target timeline, or custom specs needed..."
                            value={form.message}
                            onChange={handleChange}
                            className={`${inputCls} resize-none ${errors.message ? errorCls : ""}`}
                          />
                          {errors.message && <p className="text-[0.68rem] font-medium text-red-500">{errors.message}</p>}
                        </div>

                        {/* Submit Action */}
                        <div className="mt-1 flex flex-col gap-2">
                          {submitError && (
                            <p className="text-center text-[0.7rem] font-medium text-red-500" role="alert">
                              {submitError}
                            </p>
                          )}

                          <motion.button
                            type="submit"
                            className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-xs font-bold tracking-wider text-white shadow-md transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
                            style={{ backgroundColor: BRAND_COLOR }}
                            disabled={status === "submitting"}
                            whileHover={status !== "submitting" ? { scale: 1.005 } : {}}
                            whileTap={status !== "submitting" ? { scale: 0.995 } : {}}
                          >
                            {status === "submitting" ? (
                              <>
                                <Loader2 size={15} className="animate-spin" />
                                Processing Enquiry…
                              </>
                            ) : (
                              <>
                                <Send size={13} />
                                Submit Official Enquiry
                              </>
                            )}
                          </motion.button>

                          <p className="text-center text-[0.65rem] text-neutral-400">
                            By submitting, you agree to our privacy policy. Your information is kept strictly confidential.
                          </p>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface FieldProps {
  label: string;
  name: string;
  type: string;
  placeholder: string;
  value: string;
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputCls: string;
  errorCls: string;
  labelCls: string;
  required?: boolean;
}

const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      label,
      name,
      type,
      placeholder,
      value,
      error,
      onChange,
      inputCls,
      errorCls,
      labelCls,
      required,
    },
    ref
  ) => (
    <div className="flex min-w-0 flex-col gap-1">
      <label className={labelCls} htmlFor={name}>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        ref={ref}
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`${inputCls} ${error ? errorCls : ""}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {error && (
        <p id={`${name}-error`} className="text-[0.68rem] font-medium text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  )
);
Field.displayName = "Field";